// import DEMO from '@/assets/data/development/solution-demo.json';
import type { ISolutionData } from '@/assets/types/api/Solutions';
import type {
  INavigation,
  TSolutionsComponents,
  TThemeStyle,
} from '@/assets/types/Solutions';
import type { IOverviewData } from '@/components/Shared/Suite/Overview/index.vue';

enum EnumSolutionsLeadType {
  BOOK_A_DEMO = 'book-a-demo',
  CONTACT_SALES = 'contact-sales',
}

// 表單

interface IForm {
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  phoneCode: string;
  phoneNumber: string;
  country: string;
  employeeRange: number;
}

// 表單錯誤訊息管理

interface IFormErrorMessage {
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  phoneCode: string;
  phoneNumber: string;
  country: string;
  employeeRange: string;
}

const getForm = (): IForm => ({
  firstName: '',
  lastName: '',
  companyName: '',
  email: '',
  phoneCode: '',
  phoneNumber: '',
  country: '',
  employeeRange: 0,
});

const getFormErrorMessage = (): IFormErrorMessage => ({
  firstName: '',
  lastName: '',
  companyName: '',
  email: '',
  phoneCode: '',
  phoneNumber: '',
  country: '',
  employeeRange: '',
});

export const useSolutionsStore = defineStore('solutionsStore', () => {
  const nuxtApp = useNuxtApp();

  /* State */

  const form = ref<IForm>(getForm());
  const formErrorMessage = ref<IFormErrorMessage>(getFormErrorMessage());
  const solutionData = ref<ISolutionData | null>(null);
  const isShowMobileMenu = ref<boolean>(false);
  const isShowResult = ref<boolean>(false);
  const isSubmitting = ref<boolean>(false);

  // 目前有效的 Solutions Components

  const COMPONENTS = [
    'architecture',
    'faq',
    'overview',
    'successful-case',
    'suite-product',
    'dfsi',
    'list-products',
    'list-metrics-stacked',
    'list-metrics-split',
    'list-features-stacked',
    'list-features-split',
    'hardwares-tabs',
    'hardwares-split',
    'resources-tabs',
    'resources-split',
    'take-next-step',
  ];

  /* Getters */

  const themeStyle = computed((): TThemeStyle => {
    return solutionData.value?.banner?.themeStyle || 'wise';
  });

  const themeMainColor = computed((): string => {
    switch (themeStyle.value) {
      case 'sustainable':
        return 'text-green-400';
      case 'connected':
        return 'text-cyan-400';
      case 'ai':
        return 'text-purple-400';
      case 'operations':
        return 'text-yellow-400';
      default:
        return 'text-blue-400';
    }
  });

  const contextSchema = computed((): string[] => {
    if (solutionData.value && solutionData.value.schema) {
      return solutionData.value.schema.filter((item) => {
        if (!solutionData.value || !solutionData.value[item]) return false;

        const component = solutionData.value[item] as TSolutionsComponents;
        return COMPONENTS.includes(component.component);
      });
    }
    return [];
  });

  const contextComponents = computed((): TSolutionsComponents[] => {
    return contextSchema.value.reduce(
      (prev: TSolutionsComponents[], current: string) => {
        if (solutionData.value && solutionData.value[current]) {
          const component = solutionData.value[current] as TSolutionsComponents;
          return [...prev, component];
        }
        return prev;
      },
      [] as TSolutionsComponents[]
    );
  });

  const overviewData = computed((): IOverviewData | undefined => {
    return contextComponents.value.find(
      (component) => component.component === 'overview'
    )?.data;
  });

  const navigation = computed((): INavigation[] => {
    return contextSchema.value.reduce(
      (prev: INavigation[], current: string, index: number) => {
        if (solutionData.value !== null && solutionData.value[current]) {
          const navigable: string | undefined = (
            solutionData.value[current] as TSolutionsComponents
          ).meta?.navigable;

          const id = provideSolutionsID(navigable, index);

          if (navigable && id) {
            const result: INavigation = { id, name: navigable };
            return [...prev, result];
          }
        }
        return prev;
      },
      [] as INavigation[]
    );
  });

  /* Actions */

  const USE_GET_SOLUTIONS = async () => {
    /**
     * 取得指定解決方案頁面資訊
     */
    const { $i18n, $notification } = nuxtApp;
    const { locale } = $i18n;
    const route = useRoute();

    const { solution, generic } = route.params;
    const solutionAlias = generic ? `${solution}/${generic}` : solution;

    return await useCustomFetch(
      () =>
        `/marketplace/api/v1/solutions/${solutionAlias}?websiteCode=${locale.value}`,
      {
        method: 'GET',
        lazy: true,
        immediate: true,
        onResponse: ({ response }) => {
          const { _data, status } = response;

          if (status >= 200 && status < 300) {
            if (typeof _data === 'string') {
              solutionData.value = JSON.parse(_data);
              /**
               * * NOTE: 如果要檢視所有的 Solution Components
               * * 可以引入 '@/assets/data/development/solution-demo.json' 假資料
               * * 並寫入 this.solutionData 來做測試
               *  solutionData.value = DEMO as unknown as ISolutionData;
               */
            }
          } else {
            console.error(_data);
            $notification({
              message: (_data as { message: string }).message,
              type: 'error',
            });
          }
        },
      }
    );
  };

  const POST_SOLUTIONS_LEADS = async (
    leadTypeCode: EnumSolutionsLeadType,
    formData: FormData
  ): Promise<void> => {
    /**
     * 送出解決方案 Leads 收集相關表單
     */
    const { $customFetch, $notification, $i18n } = nuxtApp;
    const { locale } = $i18n;

    const route = useRoute();
    const { solution, generic } = route.params;
    const solutionAlias = generic ? `${solution}/${generic}` : solution;

    try {
      await $customFetch(
        `/marketplace/api/v1/leads/solutions/${leadTypeCode}/${solutionAlias}?websiteCode=${locale.value}`,
        { method: 'POST', body: formData }
      );
    } catch (error) {
      const message = (error as { message: string }).message || '';
      $notification({ message, type: 'error' });

      if (message === 'The Phone field is not a valid phone number.') {
        form.value.phoneNumber = '';
      }
      return Promise.reject(error);
    }
  };

  const FILL_FORM_WITH_PROFILE = (): void => {
    /**
     * 用戶已登入時表單自動代入資料
     */

    const profileStore = useProfileStore();
    const { profile } = storeToRefs(profileStore);

    if (!profile.value.isLogin) return;

    form.value.firstName = profile.value.firstName ?? '';
    form.value.lastName = profile.value.lastName ?? '';
    form.value.email = profile.value.email ?? '';
    form.value.phoneCode = profile.value.mobileCountryCode ?? '';
    form.value.phoneNumber = profile.value.mobileNumber ?? '';
    form.value.companyName = profile.value.companyName ?? '';
    form.value.country = profile.value.country ?? '';
  };

  const VALID_FORM_FIRST_NAME = (): boolean => {
    /**
     * 驗證 firstName 欄位，根據錯誤類型設定 ErrorMessage 文字
     * 並回傳驗證狀態（驗證通過回傳 True）
     */
    const { $i18n } = nuxtApp;
    const { t } = $i18n;

    if (form.value.firstName === '') {
      formErrorMessage.value.firstName = t('form.error-required-message');
      return false;
    }

    formErrorMessage.value.firstName = '';
    return true;
  };

  const VALID_FORM_LAST_NAME = (): boolean => {
    /**
     * 驗證 lastName 欄位，根據錯誤類型設定 ErrorMessage 文字
     * 並回傳驗證狀態（驗證通過回傳 True）
     */
    const { $i18n } = nuxtApp;
    const { t } = $i18n;

    if (form.value.lastName === '') {
      formErrorMessage.value.lastName = t('form.error-required-message');
      return false;
    }

    formErrorMessage.value.lastName = '';
    return true;
  };

  const VALID_FORM_COMPANY_NAME = (): boolean => {
    /**
     * 驗證 companyName 欄位，根據錯誤類型設定 ErrorMessage 文字
     * 並回傳驗證狀態（驗證通過回傳 True）
     */
    const { $i18n } = nuxtApp;
    const { t } = $i18n;

    if (form.value.companyName === '') {
      formErrorMessage.value.companyName = t('form.error-required-message');
      return false;
    }

    formErrorMessage.value.companyName = '';
    return true;
  };

  const VALID_FORM_EMAIL = (): boolean => {
    /**
     * 驗證 email 欄位，根據錯誤類型設定 ErrorMessage 文字
     * 並回傳驗證狀態（驗證通過回傳 True）
     */
    const { $i18n } = nuxtApp;
    const { t } = $i18n;

    if (form.value.email === '') {
      formErrorMessage.value.email = t('form.error-required-message');
      return false;
    }

    if (!validateCompanyEmail(form.value.email)) {
      formErrorMessage.value.email = t('form.error-email-message');
      return false;
    }

    formErrorMessage.value.email = '';
    return true;
  };

  const VALID_FORM_PHONE_CODE = (): boolean => {
    /**
     * 驗證 phoneCode 欄位，根據錯誤類型設定 ErrorMessage 文字
     * 並回傳驗證狀態（驗證通過回傳 True）
     */
    const { $i18n } = nuxtApp;
    const { t } = $i18n;

    if (form.value.phoneCode === '') {
      formErrorMessage.value.phoneCode = t('form.error-required-message');
      return false;
    }

    formErrorMessage.value.phoneCode = '';
    return true;
  };

  const VALID_FORM_PHONE_NUMBER = (): boolean => {
    /**
     * 驗證 phoneNumber 欄位，根據錯誤類型設定 ErrorMessage 文字
     * 並回傳驗證狀態（驗證通過回傳 True）
     */
    const { $i18n } = nuxtApp;
    const { t } = $i18n;

    if (form.value.phoneNumber === '') {
      formErrorMessage.value.phoneNumber = t('form.error-required-message');
      return false;
    }

    const isNumeric = /^\d+$/.test(form.value.phoneNumber);
    if (!isNumeric) {
      formErrorMessage.value.phoneNumber = t('form.error-numeric-only');
      return false;
    }

    formErrorMessage.value.phoneNumber = '';
    return true;
  };

  const VALID_FORM_COUNTRY = (): boolean => {
    /**
     * 驗證 country 欄位，根據錯誤類型設定 ErrorMessage 文字
     * 並回傳驗證狀態（驗證通過回傳 True）
     */
    const { $i18n } = nuxtApp;
    const { t } = $i18n;

    if (form.value.country === '') {
      formErrorMessage.value.country = t('form.error-required-message');
      return false;
    }

    formErrorMessage.value.country = '';
    return true;
  };

  const VALID_FORM_EMPLOYEES_RANGE = (): boolean => {
    /**
     * 驗證 employeeRange 欄位，根據錯誤類型設定 ErrorMessage 文字
     * 並回傳驗證狀態（驗證通過回傳 True）
     */
    const { $i18n } = nuxtApp;
    const { t } = $i18n;

    const mainStore = useMainStore();
    const { employeeRanges } = storeToRefs(mainStore);

    if (form.value.employeeRange === 0) {
      formErrorMessage.value.employeeRange = t('form.error-required-message');
      return false;
    }

    const isValidRange = employeeRanges.value.some(
      (range) => range.id === form.value.employeeRange
    );

    if (!isValidRange) {
      formErrorMessage.value.employeeRange = t('form.error-invalid-selection');
      return false;
    }

    formErrorMessage.value.employeeRange = '';
    return true;
  };

  const VALID_FORM = (): boolean => {
    /**
     * 驗證所有欄位（驗證通過回傳 True）
     */

    const result = [
      VALID_FORM_FIRST_NAME(),
      VALID_FORM_LAST_NAME(),
      VALID_FORM_COMPANY_NAME(),
      VALID_FORM_EMAIL(),
      VALID_FORM_PHONE_CODE(),
      VALID_FORM_PHONE_NUMBER(),
      VALID_FORM_COUNTRY(),
      VALID_FORM_EMPLOYEES_RANGE(),
    ];

    return result.every((value) => value === true);
  };

  const RESET_FORM = (): void => {
    /**
     * 重置表單
     */

    form.value = getForm();
    formErrorMessage.value = getFormErrorMessage();
  };

  return {
    form,
    formErrorMessage,
    solutionData,
    isShowMobileMenu,
    isShowResult,
    isSubmitting,
    contextSchema,
    contextComponents,
    overviewData,
    navigation,
    themeStyle,
    themeMainColor,
    USE_GET_SOLUTIONS,
    POST_SOLUTIONS_LEADS,
    FILL_FORM_WITH_PROFILE,
    VALID_FORM_FIRST_NAME,
    VALID_FORM_LAST_NAME,
    VALID_FORM_COMPANY_NAME,
    VALID_FORM_EMAIL,
    VALID_FORM_PHONE_CODE,
    VALID_FORM_PHONE_NUMBER,
    VALID_FORM_COUNTRY,
    VALID_FORM_EMPLOYEES_RANGE,
    VALID_FORM,
    RESET_FORM,
  };
});

export { EnumSolutionsLeadType };
